import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { URLs } from "../../utils/apis";
import {
  Box,
  CopyButton,
  Flex,
  PasswordInput,
  Select,
  Text,
  TextInput,
  Tooltip,
  ActionIcon,
  useMantineTheme,
} from "@mantine/core";

import { useMediaQuery } from "@mantine/hooks";
import moment from "moment-timezone";
import CustomButton from "../mantineCustomComponents/CustomButton";
import StampNotificationSettings from "./StampNotificationSettings";
import { changeTimezone, changeStorage } from "../../utils/userProfile";
import CustomTooltip from "../mantineCustomComponents/CustomTooltip";
import { useStylesTooltip } from "../../utils/mantine/useStylesTooltip";
import { useStylesProfileSelect } from "../../utils/mantine/useStylesProfileSelect";
import { useStylesProfilePassword } from "../../utils/mantine/useStylesProfilePassword";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import { useStylesIcons } from "../../utils/mantine/useStylesIcons";

import { useStylesUserProfileAccountSettings } from "../../utils/mantine/useStylesUserProfileAccountSettings";

const allTimezones = moment.tz.names().map((timezone) => {
  const offset = moment.tz(timezone).utcOffset();
  const currentTime = moment.tz(timezone).format("h:mmA");
  const label = ` ${timezone.padEnd(5)}   -   ${currentTime.padStart(5)}`;
  return {
    value: timezone,
    label,
    offset,
  };
});

const priorityTimezones = [
  "UTC",
  "US/Eastern",
  "US/Central",
  "US/Mountain",
  "US/Pacific",
  "Asia/Shanghai",
  "Asia/Kolkata",
  "Europe/London",
  "Europe/Paris",
  "Etc/GMT",
  "Etc/GMT+0",
  "Etc/GMT+1",
  "Etc/GMT+10",
  "Etc/GMT+11",
  "Etc/GMT+12",
  "Etc/GMT+2",
  "Etc/GMT+3",
  "Etc/GMT+4",
  "Etc/GMT+5",
  "Etc/GMT+6",
  "Etc/GMT+7",
  "Etc/GMT+8",
  "Etc/GMT+9",
  "Etc/GMT-0",
  "Etc/GMT-1",
  "Etc/GMT-10",
  "Etc/GMT-11",
  "Etc/GMT-12",
  "Etc/GMT-13",
  "Etc/GMT-14",
  "Etc/GMT-2",
  "Etc/GMT-3",
  "Etc/GMT-4",
  "Etc/GMT-5",
  "Etc/GMT-6",
  "Etc/GMT-7",
  "Etc/GMT-8",
  "Etc/GMT-9",
  "Etc/GMT0",
  "Etc/Greenwich",
  "Etc/UCT",
  "Etc/UTC",
  "Etc/Universal",
  "Etc/Zulu",
];

const sortedTimezones = allTimezones.sort((a, b) => {
  const indexA = priorityTimezones.indexOf(a.value);
  const indexB = priorityTimezones.indexOf(b.value);

  if (indexA !== -1 && indexB !== -1) {
    return indexA - indexB;
  }

  if (indexA !== -1) return -1;
  if (indexB !== -1) return 1;

  return a.value.localeCompare(b.value);
});

const AccountSettings = ({
  onTimeZone,
  timeZone,
  onDataStorage,
  dataStorage,
}) => {
  const tabletMediaQuery = useMediaQuery("(max-width: 64em)");

  const { classes: tooltipClasses } = useStylesTooltip();

  const { classes: profileSelectClasses } = useStylesProfileSelect();

  const { classes: profilePasswordClasses } = useStylesProfilePassword();

  const { classes: classesUserProfileAccountSettings } =
    useStylesUserProfileAccountSettings();

  const { classes: classesIcons } = useStylesIcons();

  const [isLoading, setIsLoading] = useState(true); // Loading state

  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();

  // Initial state for input values
  const [inputValues, setInputValues] = useState({
    api_key: "",
    private_key: "",
  });

  // State for MFA status
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState(null);

  // Fetch user data from API
  useEffect(() => {
    const fetchQuotasData = async () => {
      try {
        const response = await fetch(URLs.PROFILE_API_KEYS_URL);
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();

        setInputValues({
          api_key: data.api_key || "",
          private_key: data.private_key || "",
        });
        setMfaEnabled(data.mfa_enabled || false);
        setNotificationSettings(data.stamp_notification_settings || {});
        onTimeZone(data.display_timezone);
        onDataStorage(data.storage_type);
        setIsLoading(false);
      } catch {
        setIsLoading(false);
      }
    };

    fetchQuotasData();
  }, []); // Empty dependency array means this effect runs once on mount

  // All user storages
  const dataStorages = [
    { value: "s3", label: "Yes" },
    { value: "", label: "No" },
  ];

  if (isLoading) {
    return (
      <Box>
        <Flex
          justify={theme.flex.justify.center}
          align={theme.flex.align.center}
          styles={() => ({
            root: {
              minHeight: 400,
            },
          })}
        >
          Loading...
        </Flex>
      </Box>
    ); // Show a loading message while data is being fetched
  }

  return (
    <Box
      className={`billing ${classesUserProfileAccountSettings.wrap}`}
      style={() => ({
        paddingTop: 83,
        paddingBottom: 100,
      })}
    >
      <Flex
        gap={20}
        mb={theme.margin.xxl}
        align={theme.flex.align.center}
        className={profilePasswordClasses.wrap}
        wrap={tabletMediaQuery ? "wrap" : "nowrap"}
        justify="flex-start"
      >
        <PasswordInput
          classNames={() => ({
            root: profilePasswordClasses.root,
            label: profilePasswordClasses.label,
            input: profilePasswordClasses.input,
            wrapper: profilePasswordClasses.wrapper,
          })}
          label="API Key:"
          readOnly
          value={inputValues["api_key"]}
        />

        <CopyButton value={inputValues["api_key"]} timeout={2000}>
          {({ copied, copy }) => (
            <>
              <Tooltip
                label={copied ? "Copied" : "Copy"}
                className={classesIcons.copy}
                withArrow
                position="right"
              >
                <ActionIcon
                  color={
                    copied
                      ? theme.customColors.actionIconFirst
                      : theme.customColors.actionIconSecond
                  }
                  variant="subtle"
                  onClick={copy}
                >
                  {copied ? (
                    <IconCheck className={classesIcons.width} />
                  ) : (
                    <IconCopy className={classesIcons.width} />
                  )}
                </ActionIcon>
              </Tooltip>
              &nbsp;&nbsp;
            </>
          )}
        </CopyButton>
      </Flex>
      <Flex
        gap={20}
        mb={theme.margin.xxl}
        align={theme.flex.align.center}
        className={profilePasswordClasses.wrap}
        wrap={tabletMediaQuery ? "wrap" : "nowrap"}
        justify="flex-start"
      >
        <PasswordInput
          classNames={() => ({
            root: profilePasswordClasses.root,
            label: profilePasswordClasses.label,
            input: profilePasswordClasses.input,
            wrapper: profilePasswordClasses.wrapper,
          })}
          label="Private Key:"
          readOnly
          value={inputValues["private_key"]}
        />

        <CopyButton value={inputValues["private_key"]} timeout={2000}>
          {({ copied, copy }) => (
            <>
              <Tooltip
                label={copied ? "Copied" : "Copy"}
                className={classesIcons.copy}
                withArrow
                position="right"
              >
                <ActionIcon
                  color={
                    copied
                      ? theme.customColors.actionIconFirst
                      : theme.customColors.actionIconSecond
                  }
                  variant="subtle"
                  onClick={copy}
                >
                  {copied ? (
                    <IconCheck className={classesIcons.width} />
                  ) : (
                    <IconCopy className={classesIcons.width} />
                  )}
                </ActionIcon>
              </Tooltip>
              &nbsp;&nbsp;
            </>
          )}
        </CopyButton>
      </Flex>

      <Flex
        gap={20}
        mb={theme.margin.xxl}
        align={theme.flex.align.center}
        wrap={tabletMediaQuery ? "wrap" : "nowrap"}
        justify="flex-start"
      >
        <Select
          label={
            <Flex align={theme.flex.align.center} gap={theme.flex.gap.small}>
              Display Timezone
              <CustomTooltip
                content={
                  <>
                    <Text
                      classNames={{
                        root: tooltipClasses.tooltipText,
                      }}
                    >
                      Specify the timezone that will be used to display
                      timestamps for you across the vBase application. You can
                      select timezones tied to locations (e.g. New York) or tied
                      to universal time, e.g. (GMT-5)
                    </Text>
                  </>
                }
              ></CustomTooltip>
            </Flex>
          }
          className="select-display-timezone"
          classNames={() => ({
            root: profileSelectClasses.root,
            label: profileSelectClasses.label,
            input: profileSelectClasses.input,
            wrapper: profileSelectClasses.wrapper,
          })}
          data={sortedTimezones}
          value={timeZone}
          onChange={onTimeZone}
          clearable
          searchable
        />
        <CustomButton
          text="Save"
          componentClasses="link button-display-timezone"
          type="button"
          variant="transparent"
          onClick={() => {
            changeTimezone(timeZone);
          }}
        />
      </Flex>

      <Flex
        gap={20}
        mb={theme.margin.xxl}
        align={theme.flex.align.center}
        wrap={tabletMediaQuery ? "wrap" : "nowrap"}
        justify="flex-start"
      >
        <Select
          label={
            <Flex align={theme.flex.align.center} gap={theme.flex.gap.small}>
              Store Stamped Files
              <CustomTooltip
                content={
                  <>
                    <Text
                      classNames={{
                        root: tooltipClasses.tooltipText,
                      }}
                    >
                      A secure backup copy of your stamped data is needed for
                      validation.
                    </Text>
                    <Text
                      classNames={{
                        root: tooltipClasses.tooltipText,
                      }}
                    >
                      Select Yes in the dropdown menu if you’d like vBase to
                      maintain a backup of data stamped via our web application.
                    </Text>
                    <Text
                      classNames={{
                        root: tooltipClasses.tooltipText,
                      }}
                    >
                      Select No if you will maintain your own backups. In this
                      case, vBase will not see or store a copy of your data.
                    </Text>
                  </>
                }
              ></CustomTooltip>
            </Flex>
          }
          className="select-user-data-storage"
          classNames={() => ({
            root: profileSelectClasses.root,
            label: profileSelectClasses.label,
            input: profileSelectClasses.input,
            wrapper: profileSelectClasses.wrapper,
          })}
          data={dataStorages}
          value={dataStorage}
          onChange={onDataStorage}
          clearable
          searchable
        />
        <CustomButton
          text="Save"
          componentClasses="link button-user-data-storage"
          type="button"
          variant="transparent"
          onClick={() => {
            changeStorage(dataStorage);
          }}
        />
      </Flex>
      <Flex
        gap={20}
        mb={theme.margin.xxl}
        align={theme.flex.align.center}
        wrap={tabletMediaQuery ? "wrap" : "nowrap"}
        justify="flex-start"
      >
        <TextInput
          label={
            <Flex
              className="label-mfa-action"
              align={theme.flex.align.center}
              gap={theme.flex.gap.small}
            >
              Two-Factor Authentication
              <CustomTooltip
                content={
                  <>
                    <Text
                      classNames={{
                        root: tooltipClasses.tooltipText,
                      }}
                    >
                      Two-factor authentication adds an extra layer of security
                      to your account by requiring a second form of verification
                      in addition to your password.
                    </Text>
                  </>
                }
              ></CustomTooltip>
            </Flex>
          }
          className={`select-mfa-status ${
            mfaEnabled
              ? "select-mfa-status-active"
              : "select-mfa-status-inactive"
          }`}
          classNames={() => ({
            root: profileSelectClasses.root,
            label: profileSelectClasses.label,
            input: profileSelectClasses.input,
            wrapper: profileSelectClasses.wrapper,
          })}
          value={`Status: ${mfaEnabled ? "Active" : "Inactive"}`}
          readOnly
        />
        <CustomButton
          text={mfaEnabled ? "Deactivate" : "Activate"}
          componentClasses="link button-mfa-action"
          type="button"
          variant="transparent"
          onClick={() => {
            window.location.href = mfaEnabled
              ? "/accounts/2fa/totp/deactivate/"
              : "/accounts/2fa/totp/activate/";
          }}
        />
      </Flex>
      {notificationSettings !== null ? (
        <StampNotificationSettings initialSettings={notificationSettings} />
      ) : (
        <Text c="red">
          Email settings could not be loaded. Reload the page to try again.
        </Text>
      )}
    </Box>
  );
};

AccountSettings.propTypes = {
  onTimeZone: PropTypes.func.isRequired,
  timeZone: PropTypes.string,
  onDataStorage: PropTypes.func.isRequired,
  dataStorage: PropTypes.string,
};

export default AccountSettings;
